import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Ticket as TicketIcon, Filter, Radio, CheckCircle2, XCircle, Clock } from "lucide-react";

const STATUS = {
  waiting: { label: "En attente", cls: "bg-[#FFFBE6] text-[#9A7D00]" },
  called: { label: "Appelé", cls: "bg-brand/10 text-brand", icon: Clock },
  present: { label: "Servi", cls: "bg-[#ECFBEF] text-[#0A7F2E]", icon: CheckCircle2 },
  absent: { label: "Absent/Annulé", cls: "bg-surface text-subtle", icon: XCircle },
};

export default function AdminTickets() {
  const { role, user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [commerces, setCommerces] = useState([]);
  const [filters, setFilters] = useState({ status: "", commerceId: "", dateFrom: "", dateTo: "" });
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (role !== "client" || !user?.isAdmin) { nav("/"); return; }
    api.get("/admin/commerces-list").then((r) => setCommerces(r.data)).catch(() => setCommerces([]));
    load({});
  }, [loading, role, user, nav]); // eslint-disable-line

  const load = async (f = filters) => {
    setLoadingData(true);
    try {
      const params = {};
      if (f.status) params.status = f.status;
      if (f.commerceId) params.commerceId = f.commerceId;
      if (f.dateFrom) params.dateFrom = new Date(f.dateFrom).toISOString();
      if (f.dateTo) {
        const d = new Date(f.dateTo);
        d.setHours(23, 59, 59, 999);
        params.dateTo = d.toISOString();
      }
      const { data } = await api.get("/admin/tickets", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoadingData(false);
    }
  };

  const onFilterChange = (k, v) => setFilters((s) => ({ ...s, [k]: v }));
  const reset = () => {
    const f = { status: "", commerceId: "", dateFrom: "", dateTo: "" };
    setFilters(f);
    load(f);
  };

  const counts = useMemo(() => {
    const c = { waiting: 0, called: 0, present: 0, absent: 0 };
    (items || []).forEach((t) => { if (c[t.status] !== undefined) c[t.status]++; });
    return c;
  }, [items]);

  if (items === null) return <div className="container mx-auto py-16 text-center text-subtle">Chargement…</div>;

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 md:py-14 max-w-7xl">
      <p className="overline mb-2">Admin · Tickets</p>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-ink">Historique global des tickets</h1>
      <p className="mt-2 text-subtle">{total} ticket{total > 1 ? "s" : ""} correspondant{total > 1 ? "s" : ""} aux filtres.</p>

      {/* Filters */}
      <div className="mt-8 border border-border" data-testid="filters-panel">
        <div className="p-4 border-b border-border bg-surface flex items-center gap-2">
          <Filter size={16} className="text-brand" />
          <span className="font-display font-bold text-ink text-sm">Filtres</span>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="overline block mb-1">Statut</label>
            <select
              value={filters.status}
              onChange={(e) => onFilterChange("status", e.target.value)}
              className="w-full h-10 border border-border bg-white px-3 text-sm focus:outline-none focus:border-ink"
              data-testid="filter-status"
            >
              <option value="">Tous</option>
              <option value="waiting">En attente</option>
              <option value="called">Appelé</option>
              <option value="present">Servi</option>
              <option value="absent">Absent/Annulé</option>
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Établissement</label>
            <select
              value={filters.commerceId}
              onChange={(e) => onFilterChange("commerceId", e.target.value)}
              className="w-full h-10 border border-border bg-white px-3 text-sm focus:outline-none focus:border-ink"
              data-testid="filter-commerce"
            >
              <option value="">Tous</option>
              {commerces.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.city ? ` · ${c.city}` : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Du</label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => onFilterChange("dateFrom", e.target.value)}
              className="h-10 rounded-none border-border"
              data-testid="filter-date-from"
            />
          </div>
          <div>
            <label className="overline block mb-1">Au</label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => onFilterChange("dateTo", e.target.value)}
              className="h-10 rounded-none border-border"
              data-testid="filter-date-to"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => load()}
              disabled={loadingData}
              className="h-10 flex-1 bg-brand hover:bg-brand-hover text-white rounded-none"
              data-testid="apply-filters-btn"
            >
              {loadingData ? "…" : "Appliquer"}
            </Button>
            <Button
              onClick={reset}
              variant="outline"
              className="h-10 rounded-none border-border"
              data-testid="reset-filters-btn"
            >
              Réinit.
            </Button>
          </div>
        </div>
      </div>

      {/* Counts */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 border border-border" data-testid="counts-grid">
        <Count label="En attente" value={counts.waiting} />
        <Count label="Appelés" value={counts.called} border />
        <Count label="Servis" value={counts.present} border />
        <Count label="Absents" value={counts.absent} border />
      </div>

      {/* Table */}
      <div className="mt-6">
        {items.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center">
            <TicketIcon className="mx-auto text-subtle" size={36} />
            <p className="mt-4 text-ink font-medium">Aucun ticket trouvé pour ces filtres</p>
          </div>
        ) : (
          <div className="border border-border divide-y divide-border">
            {items.map((t) => {
              const s = STATUS[t.status] || STATUS.waiting;
              return (
                <div key={t.id} className="grid grid-cols-12 gap-3 items-center p-4" data-testid={`admin-ticket-row-${t.id}`}>
                  <div className="col-span-2 md:col-span-1 font-mono text-xl font-bold text-ink">#{t.number}</div>
                  <div className="col-span-10 md:col-span-4 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display font-bold text-ink truncate">{t.commerceName}</p>
                      {t.type === "priority" && (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-brand uppercase tracking-wider">
                          <Radio size={11} /> Distance
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-subtle mt-0.5 truncate">
                      {t.commerceType}{t.commerceCity ? ` · ${t.commerceCity}` : ""}
                    </p>
                  </div>
                  <div className="col-span-7 md:col-span-4 min-w-0">
                    <p className="text-sm text-ink truncate">{t.userName}</p>
                    <p className="text-xs text-subtle truncate">{t.userEmail}</p>
                  </div>
                  <div className="col-span-5 md:col-span-2 text-xs text-subtle text-right md:text-left">
                    {new Date(t.createdAt).toLocaleString("fr-FR")}
                  </div>
                  <div className="col-span-12 md:col-span-1 flex md:justify-end">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold uppercase tracking-wider ${s.cls}`}>
                      {s.icon && <s.icon size={11} />} {s.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Count({ label, value, border }) {
  return (
    <div className={`p-4 ${border ? "border-l border-border" : ""}`}>
      <p className="overline">{label}</p>
      <p className="mt-1 font-mono text-2xl font-bold text-ink">{value}</p>
    </div>
  );
}
